import { AxiosError, AxiosResponse } from 'axios'
import format from 'date-fns/format'
import { TFunction } from 'next-i18next'
import {
  TOTAL_COUNT_HEADER,
  PAGE_COUNT_HEADER,
} from '@fe/common/constants/headers'

export const formatRequestData = (
  dataElement: Record<string, unknown>,
  // Legacy endpoints expect booleans as 0|1; the FINTWIN applications API
  // validates its confirmation gates against a strict JSON `true`, so those
  // requests opt out by passing `false` here.
  coerceBooleans = true
) => {
  for (const [key, value] of Object.entries(dataElement)) {
    if (value == null) {
      continue
    }

    if (value instanceof Date) {
      const dateString = format(value, 'yyyy-MM-dd')

      dataElement[key] = dateString

      continue
    }

    if (typeof value === 'object' && !(value instanceof Array)) {
      formatRequestData(value as Record<string, unknown>, coerceBooleans)

      dataElement[key] = value

      continue
    }

    if (value instanceof Array && !value[0]?.label && !value[0]?.value) {
      for (const element of value) {
        if (typeof element === 'string') {
          continue
        }

        formatRequestData(element, coerceBooleans)
      }

      dataElement[key] = value

      continue
    }

    if (typeof value === 'string' && !(dataElement instanceof Array)) {
      dataElement[key] = value.trimStart().trimEnd()
    }

    if (coerceBooleans && typeof value === 'boolean') {
      dataElement[key] = Number(value)
    }

    if (typeof value === 'string') {
      dataElement[key] = value.trimStart().trimEnd()
    }
  }
}

type ResponseWithCount<T> = {
  data: T & {
    total_count: number
    page_count: number
  }
} & Omit<AxiosResponse<T>, 'data'>

export const addTotalCount = <T>(
  response: AxiosResponse<T>
): ResponseWithCount<T> => {
  const { data, headers } = response

  const total_count = Number(headers[TOTAL_COUNT_HEADER])
  const page_count = Number(headers[PAGE_COUNT_HEADER])

  const modifiedData = { ...data, total_count, page_count }

  return { ...response, data: modifiedData }
}

// The backend error body is a JSend-ish blob; axios >=0.27 types
// `response.data` as `unknown`, so describe just the fields we read off it.
interface ApiErrorData {
  status?: string
  message?: string
  data?: Array<{ message?: string[] }>
}

export const formatResponseError = (error: AxiosError, t?: TFunction) => {
  const responseData = error.response?.data as ApiErrorData

  // No response at all: axios' own message is a bare "Network Error", which
  // tells the user nothing. Say what happened and what to do instead.
  if (t && error.isAxiosError && !error.response) {
    return t('common:network-error')
  }

  return (
    (responseData?.status === 'error' &&
      JSON.stringify(responseData.message)) ||
    (responseData?.status === 'fail' && responseData.data?.[0]?.message?.[0]) ||
    // responseData.data[0]?.message?.[0] || //Q.V. CL-586
    responseData?.message ||
    (Array.isArray(responseData) && responseData[0]?.message?.[0]) ||
    error.message ||
    t?.('common:unknown-error')
  )
}

// Backend 6.20: the client's requisites (deposit details, crypto addresses)
// are not issued yet. It comes as an HTTP 200 JSend error envelope.
export const NOT_ISSUED_CODE = 1612

/**
 * True when a request was refused because the requisites are not issued yet.
 * Reads both shapes: the HTTP 200 envelope, which the response interceptor
 * rejects with the transformed body itself (`{ message, status, code }`), and
 * an AxiosError whose response body carries the code.
 */
export const isNotIssued = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false
  }

  const { code, response } = error as {
    code?: unknown
    response?: { data?: { code?: unknown } }
  }

  return code === NOT_ISSUED_CODE || response?.data?.code === NOT_ISSUED_CODE
}
